import { api } from './apiClient';

/**
 * Photo Quotes — a visual selection of products prepared for a customer, or for a sales agent
 * (optionally naming the customer the agent will order for). The recipient opens it in the
 * Trade Portal, amends quantities, removes lines and orders from it.
 * Backend: backend/src/routes/v1/quotes.js.
 */

export type PhotoQuoteStatus = 'draft' | 'sent' | 'viewed' | 'ordered' | 'cancelled';
export type PhotoQuoteRecipientType = 'customer' | 'agent';

export interface PhotoQuoteItem {
  id: number;
  quote_id: string;
  zoho_item_id: string;
  product_id: number | null;
  sku: string;
  name: string;
  brand: string | null;
  brand_name: string | null;
  image_url: string | null;
  /** Every picture the product had when the quote was built, main image first. */
  image_urls: string[];
  pack_qty: number;
  unit_price: string;
  quantity: number;
  /** What the customer changed it to; null means they left the suggested quantity alone. */
  customer_quantity: number | null;
  removed: boolean;
  position: number;
}

export interface PhotoQuoteOrder {
  id: number;
  quote_id: string;
  order_id: number | null;
  client_order_id: string | null;
  salesorder_number: string | null;
  brand: string | null;
  order_number?: string | null;
  order_status?: string | null;
  order_total?: number | null;
  created_at: string;
}

export interface PhotoQuoteSummary {
  id: string;
  quote_number: string;
  status: PhotoQuoteStatus;
  title: string | null;
  valid_until: string | null;
  sent_at: string | null;
  ordered_at: string | null;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
  recipient_type: PhotoQuoteRecipientType;
  recipient_agent_id: string | null;
  recipient_agent_name: string | null;
  /** Always set on a customer quote; optional on an agent quote. */
  customer_id: number | null;
  company_name: string | null;
  contact_name: string | null;
  customer_email: string | null;
  line_count: number;
  total_value: string;
}

export interface PhotoQuote extends Omit<PhotoQuoteSummary, 'line_count' | 'total_value'> {
  zoho_customer_id: string | null;
  recipient_agent_email: string | null;
  message: string | null;
  first_viewed_at: string | null;
  last_viewed_at: string | null;
  items: PhotoQuoteItem[];
  orders: PhotoQuoteOrder[];
}

export interface QuoteItemInput {
  zoho_item_id: string;
  quantity: number;
  /** Optional override; omit to use the product's list rate. */
  unit_price?: number | null;
}

export interface QuoteFilters {
  status?: string;
  customer_id?: number;
  recipient_agent_id?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

/** Who a quote goes to. customer_id is required for a customer quote, optional for an agent. */
export interface QuoteRecipientInput {
  recipient_type: PhotoQuoteRecipientType;
  recipient_agent_id?: string | null;
  customer_id?: number | null;
}

export interface CreateQuotePayload extends QuoteRecipientInput {
  title?: string | null;
  message?: string | null;
  valid_until?: string | null;
  items: QuoteItemInput[];
}

/** Recipient fields are accepted only while the quote is still a draft; send all three together. */
export type UpdateQuotePayload = Partial<CreateQuotePayload> & {
  status?: 'draft' | 'cancelled';
};

interface SendResult {
  data: PhotoQuote;
  email: { success: boolean; error?: string; message_id?: string };
}

export const quoteService = {
  async list(filters: QuoteFilters = {}): Promise<{ data: PhotoQuoteSummary[]; total: number }> {
    return api.get<{ data: PhotoQuoteSummary[]; total: number }>(
      '/api/v1/quotes',
      filters as Record<string, string | number>,
    );
  },

  async getById(id: string): Promise<PhotoQuote> {
    const result = await api.get<{ data: PhotoQuote }>(`/api/v1/quotes/${id}`);
    return result.data;
  },

  async create(payload: CreateQuotePayload): Promise<PhotoQuote> {
    const result = await api.post<{ data: PhotoQuote }>('/api/v1/quotes', payload);
    return result.data;
  },

  async update(id: string, payload: UpdateQuotePayload): Promise<PhotoQuote> {
    const result = await api.patch<{ data: PhotoQuote }>(`/api/v1/quotes/${id}`, payload);
    return result.data;
  },

  /** Mark sent and email the recipient (customer or agent) the portal deep link. */
  async send(id: string, to?: string): Promise<SendResult> {
    return api.post<SendResult>(`/api/v1/quotes/${id}/send`, to ? { to } : {});
  },

  /** Drafts are deleted outright; anything already sent is cancelled. */
  async remove(id: string): Promise<void> {
    await api.delete(`/api/v1/quotes/${id}`);
  },
};
